import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { useForm } from "react-hook-form"

import {
  INVENTORY_LOT_QUALITY_STATUS_OPTIONS,
  INVENTORY_LOT_STATUS_TO_QUALITY_STATUS,
  type InventoryLotEligibleQualityStatus,
} from "../../constants"
import { ChangeLotQualityStatusResult, useChangeLotQualityStatus } from "../../hooks/useChangeLotQualityStatus"
import { type LotQualityStatusFormValues, lotQualityStatusFormSchema } from "../../schemas"

import { Button } from "@/components/common/Button"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { FormSelect } from "@/components/UiKit/FormControls/FormSelect"
import type { InventoryByLotItem } from "@/graphql/codegen/graphql"
import { isNil } from "@/utils/utils"

interface ProductInventoryLotQualityStatusDialogProps {
  item: InventoryByLotItem
  productId: number
  onClose: () => void
  onSuccess: (status: InventoryLotEligibleQualityStatus) => void
  onLotNotFound: (message: string) => void
}

export const ProductInventoryLotQualityStatusDialog: FC<ProductInventoryLotQualityStatusDialogProps> = ({
  item,
  productId,
  onClose,
  onSuccess,
  onLotNotFound,
}) => {
  const { changeLotQualityStatus } = useChangeLotQualityStatus()

  const form = useForm<LotQualityStatusFormValues>({
    resolver: zodResolver(lotQualityStatusFormSchema),
    mode: "onChange",
    defaultValues: {
      status: isNil(item.status) ? undefined : INVENTORY_LOT_STATUS_TO_QUALITY_STATUS[item.status],
    },
  })

  const {
    control,
    formState: { isSubmitting, isValid },
  } = form

  const handleClose = () => {
    if (!isSubmitting) onClose()
  }

  const handleSubmit = form.handleSubmit(async ({ status }) => {
    const outcome = await changeLotQualityStatus({
      productId,
      lotNumber: item.lotNumber,
      batchNumber: item.batchNumber,
      status,
    })

    if (outcome.result === ChangeLotQualityStatusResult.Success) onSuccess(status)
    if (outcome.result === ChangeLotQualityStatusResult.LotNotFound) onLotNotFound(outcome.message)
  })

  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{ sx: { minWidth: 520 } }}
      data-testid='product-inventory-lot.quality-status-dialog'
    >
      <DialogHeader title='Change Quality Status' onClose={handleClose} />
      <DialogContent className='flex flex-col gap-y-6 p-10'>
        <FormSelect
          control={control}
          name='status'
          label='Status'
          required
          options={INVENTORY_LOT_QUALITY_STATUS_OPTIONS}
          data-testid='product-inventory-lot.quality-status-dialog.status-field'
        />
      </DialogContent>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button
          appearance='outlined'
          disabled={isSubmitting}
          onClick={handleClose}
          data-testid='product-inventory-lot.quality-status-dialog.cancel-button'
        >
          Cancel
        </Button>
        <Button
          appearance='contained'
          disabled={isSubmitting || !isValid}
          onClick={handleSubmit}
          data-testid='product-inventory-lot.quality-status-dialog.save-button'
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}
